<script setup lang="ts">
/**
 * 保護者が子アカウントの「作品の公開（クリスタル化）」を ON にするときの同意（#975）。
 *
 * 読み手は保護者（大人）。公開すると誰に何が見えるか・名前や学校名を書かないように話してほしいこと・
 * いつでも非公開に戻せることを示し、確認のチェックを入れたときだけ ON にできる。**文面を変えたら、サーバの
 * `CRYSTALLIZE_CONSENT_VERSION`（`services/guardian_settings.py`）を上げる**（同意した文面の版を子の User に残すので）。
 */
import LargeDialog from './LargeDialog.vue'

const dialog = defineModel<boolean>()
const props = defineProps<{ childName: string, busy?: boolean }>()
const emit = defineEmits<{ agreed: [] }>()

const checked = ref(false)

watch(dialog, (open) => {
  if (open) checked.value = false
})
</script>

<template>
  <LargeDialog
    v-model="dialog"
    title="作品を公開できるようにする"
    :cancel-button="{ title: 'やめておく' }"
    :submit-button="{ title: '同意して ON にする', disabled: !checked }"
    keep-open-on-submit
    :busy="props.busy"
    @submit="emit('agreed')"
  >
    <template #default>
      <div
        class="guardian-consent"
        data-testid="guardian-crystallize-consent"
      >
        <p>
          作った作品を魔法研究会に公開（クリスタル化）できるようにします。{{ props.childName }}さんの作品が、
          Code & Magic を使っていない人も含めて、だれでも見て遊べるようになります。
        </p>
        <ul>
          <li>公開されるのは、作品のタイトル・説明・表紙の画像・ゲームそのものと、冒険者名です。</li>
          <li>作品の設定によっては、ほかの冒険者が魔導書の中身を読んだり、新しい作品のもとにしたりできます。</li>
          <li>タイトルや説明、ゲームの中の文字や絵に、本名や学校名などを書かないよう、お子さまと話してみてください。</li>
          <li>公開した作品は、いつでも非公開に戻せます。OFF に戻すと、新しく公開できなくなります（公開済みの作品はそのまま残ります）。</li>
        </ul>
        <v-checkbox
          v-model="checked"
          hide-details
          density="comfortable"
          data-testid="guardian-crystallize-consent-check"
          label="上の内容を確認し、作品を公開することに同意します"
        />
      </div>
    </template>
  </LargeDialog>
</template>

<style scoped>
/* 共有コンポーネントなので calc(N * var(--u))（responsive_sizing.md） */
.guardian-consent {
  font-weight: normal;
  font-size: max(12px, calc(1.6 * var(--u)));
  line-height: 1.7;
  padding: 0 max(8px, calc(1.5 * var(--u)));
}

.guardian-consent ul {
  margin: max(6px, calc(0.8 * var(--u))) 0;
  padding-left: 1.2em;
}

/* 同意のチェックの文も本文と同じ大きさに（Vuetify の既定は小さい） */
.guardian-consent :deep(.v-label) {
  font-size: max(12px, calc(1.6 * var(--u)));
  opacity: 1;
}

.guardian-consent li + li {
  margin-top: max(4px, calc(0.5 * var(--u)));
}
</style>
